import { Link } from "react-router-dom";
import { Avatar, Card, IconWrapper, Tag, Typography } from "@/components/ui/atoms";
import type { BlogPostViewModel } from "../../types";
import { formatLongDate } from "@/utils/date";
import { CalendarIcon } from "lucide-react";

interface FeaturedCardProps {
  post: BlogPostViewModel;
}

export function FeaturedCard({ post }: FeaturedCardProps) {
  const authorName = post.author?.name || "Invirtus";
  const authorAvatar = post.author?.avatarUrl;

  return (
    <Card
      variant="elevated"
      padding="none"
      rounded="xl"
      className="group overflow-hidden rounded-3xl border-dark-invirtus-soft bg-light"
    >
      <Link to={`/blog/${post.slug}`} className="relative block">
        <div className="aspect-[4/5] w-full bg-light-lila md:aspect-[20/8]">
          {post.coverImageUrl ? (
            <img
              src={post.coverImageUrl}
              alt={post.coverImageAlt || post.title}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
              loading="eager"
            />
          ) : null}
        </div>
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-dark-invirtus/90 via-dark-invirtus/70 to-transparent p-4 sm:p-5 md:p-7">
          <Typography variant="h1" color="white">
            {post.title}
          </Typography>
          {!!post.excerpt && (
            <Typography as="p" variant="body-sm" color="white" className="mt-2 line-clamp-2">
              {post.excerpt}
            </Typography>
          )}
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-3 sm:gap-5">
              <div className="flex items-center gap-2">
                <Avatar
                  initials={authorName}
                  src={authorAvatar}
                  alt={authorName}
                  size="sm"
                  variant={authorAvatar ? "neutral" : "gradient"}
                />
                <Typography as="p" variant="body-sm" weight="semibold" color="white">
                  {authorName}
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <IconWrapper size="lg" color="white" className="rounded-full border border-white/35 bg-white/10 p-2">
                  <CalendarIcon />
                </IconWrapper>
                <Typography as="p" variant="caption" color="white">
                  {formatLongDate(post.publishedAt || post.createdAt)}
                </Typography>
              </div>
            </div>
            {!!post.tags?.length && (
              <div className="flex flex-wrap gap-2">
                {post.tags.slice(0, 3).map((tag) => (
                  <Tag
                    key={`${post.id}-${tag.id}`}
                    variant="subtle"
                    intent="primary"
                    palette="brand"
                    className="border border-white/35 bg-white/10 text-white"
                  >
                    {tag.name}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        </div>
      </Link>
    </Card>
  );
}
